import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react'
import type { Session } from '@supabase/supabase-js'
import { hasBackend, supabase } from './lib/supabase'
import { avvStatus, findMyTenant, setzeStufe } from './data/supabaseAdapter'
import { avvZustand, type AvvStatus } from './lib/avv'
import type { Stufe } from './lib/rollen'
import { track } from './lib/track'

const DEMO_KEY = 'staysuite.demo'

/**
 * `loeschung`: eine laufende Konto-Löschvormerkung (Vorgang 019, AK5). Sie wird
 * beim Anmelden geprüft und rendert statt der App die Wahl „Löschung abbrechen /
 * Löschung beibehalten" — derselbe Platz im Fluss, an dem Onboarding von Live
 * unterschieden wird.
 */
export type Mode = 'loading' | 'anonymous' | 'demo' | 'onboarding' | 'live' | 'loeschung' | 'avv'

interface AuthValue {
  mode: Mode
  avv: AvvStatus | null
  avvNeuLaden: () => Promise<void>
  avvGateAussetzen: () => void
  session: Session | null
  tenantId: string | null
  /**
   * Die Stufe im geladenen Betrieb — `null` in der Demo und waehrend des Ladens.
   *
   * DIAGNOSE, KEIN TOR. Sie steuert, was die Oberflaeche anbietet; geprueft wird
   * in der Datenbank (Vorgang 013, Migrationen 0070 und 0072). Wer sie im
   * Browser faelscht, sieht mehr Knoepfe und bekommt trotzdem 42501.
   */
  rolle: Stufe | null
  error: string | null
  /** Die offene Löschvormerkung des angemeldeten Kontos — sonst `null` (AK5). */
  loeschung: { fristEnde: string | null } | null
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<{ needsConfirmation: boolean }>
  signOut: () => Promise<void>
  enterDemo: () => void
  /**
   * Re-Authentifizierung vor der Konto-Löschung (Vorgang 019, AK2): dasselbe
   * `signInWithPassword` gegen die eigene Adresse, aber ohne den geteilten
   * Fehlerzustand — die Ablehnung gehört ins Löschblatt, nicht auf den
   * Anmeldeschirm. Wirft bei falschem Passwort.
   */
  reauth: (password: string) => Promise<void>
  /** AK5 „Löschung abbrechen": Widerruf per RPC, danach normaler Start. */
  loeschungAbbrechen: () => Promise<void>
  /** Called after onboarding creates the tenant. */
  setTenantId: (id: string) => void
  clearError: () => void
}

const Ctx = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [rolle, setRolle] = useState<Stufe | null>(null)
  const [ready, setReady] = useState(!hasBackend)
  const [demo, setDemo] = useState(() => localStorage.getItem(DEMO_KEY) === '1')
  const [error, setError] = useState<string | null>(null)
  const [loeschung, setLoeschung] = useState<{ fristEnde: string | null } | null>(null)
  /*
   * Für welchen Nutzer die Vormerkung schon geprüft wurde. Die Re-Auth vor der
   * Löschung (`reauth`) erzeugt eine NEUE Session desselben Nutzers — ohne diese
   * Wache liefe der Vormerkungs-Select dann noch einmal und könnte, während das
   * Löschblatt gerade die Bestätigung zeigt, die frisch angelegte Vormerkung
   * finden und die App auf den AK5-Dialog umschalten. Geprüft wird je Anmeldung,
   * nicht je Token.
   */
  const loeschungGeprueftFuer = useRef<string | null>(null)
  const [resolvedUser, setResolvedUser] = useState<string | null>(null)
  const [avv, setAvv] = useState<AvvStatus | null>(null)
  const [avvScope, setAvvScope] = useState<string | null>(null)
  const [avvSuspended, setAvvSuspended] = useState<string | null>(null)
  const [avvReload, setAvvReload] = useState(0)
  const [authEpisode, setAuthEpisode] = useState(0)
  const lastAuthUser = useRef<string | null>(null)
  const lastAuthToken = useRef<string | null>(null)
  const scope = session && tenantId ? `${session.user.id}:${tenantId}:${authEpisode}` : null
  const currentScope = useRef(scope)
  currentScope.current = scope
  useEffect(() => {
    if (!supabase || !scope || !tenantId || resolvedUser !== session?.user.id || loeschung) return
    let alive = true
    setAvv(null)
    setAvvScope(null)
    void avvStatus(supabase, tenantId).catch(() => null).then(status => {
      if (!alive || currentScope.current !== scope) return
      setAvv(status)
      setAvvScope(scope)
    })
    return () => { alive = false }
  }, [scope, tenantId, avvReload, resolvedUser, loeschung])
  const avvGateAussetzen = useCallback(() => setAvvSuspended(currentScope.current), [])
  const avvNeuLaden = useCallback(async () => {
    if (!supabase || !tenantId || !scope) return
    const status = await avvStatus(supabase, tenantId).catch(() => null)
    if (currentScope.current !== scope) return
    setAvv(status); setAvvScope(scope); setAvvSuspended(null)
  }, [tenantId, scope])

  useEffect(() => {
    if (!supabase) return
    let alive = true

    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return
      lastAuthUser.current = data.session?.user.id ?? null
      lastAuthToken.current = data.session?.access_token ?? null
      setSession(data.session)
      setReady(true)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      // Refocus may repeat SIGNED_IN for the same token; refresh alone is not a new login.
      if (lastAuthUser.current !== (next?.user.id ?? null)
        || (event === 'SIGNED_IN' && lastAuthToken.current !== (next?.access_token ?? null))) {
        setAvvSuspended(null)
        setAuthEpisode(n => n + 1)
      }
      lastAuthUser.current = next?.user.id ?? null
      lastAuthToken.current = next?.access_token ?? null
      setSession(next)
      if (!next) {
        setTenantId(null)
        setLoeschung(null)
        setAvv(null); setAvvScope(null); setAvvSuspended(null); setResolvedUser(null)
        loeschungGeprueftFuer.current = null
      }
    })
    return () => { alive = false; sub.subscription.unsubscribe() }
  }, [])

  // Resolve the tenant once a session exists. A user with no membership yet lands in
  // onboarding rather than staring at an empty calendar.
  //
  // Seit Vorgang 019 läuft daneben der AK5-Blick auf `konto_loeschungen`: die
  // RLS-Policy `konto_loeschungen_self` zeigt höchstens die eigene offene Zeile.
  // Beide Abfragen laufen parallel — der Normalfall (keine Vormerkung) darf durch
  // den neuen Select nicht langsamer werden (Testplan 019, T33). Entschieden wird
  // trotzdem VOR dem Mandanten: liegt eine Vormerkung vor, wird kein Tenant
  // gesetzt und statt der App der Dialog gerendert.
  useEffect(() => {
    if (!supabase || !session) return
    let alive = true
    const sb = supabase
    const pruefen = loeschungGeprueftFuer.current !== session.user.id
    Promise.all([
      pruefen
        ? sb.from('konto_loeschungen')
          .select('id, frist_ende')
          .is('vollzogen_at', null)
          .is('abgebrochen_at', null)
        : Promise.resolve({ data: null, error: null }),
      // B4 (Prüfbefund): ein findMyTenant-Fehler darf das AK5-Gate nicht
      // unterlaufen — würde Promise.all hier verwerfen, fiele auch eine
      // GEFUNDENE Vormerkung weg und ein Konto in laufender Löschung landete
      // im Onboarding. Der Tenant-Fehler wird stattdessen einzeln gemeldet.
      findMyTenant(sb).catch((e: Error) => { setError(e.message); return null }),
    ])
      .then(([vormerkung, m]) => {
        if (!alive) return
        loeschungGeprueftFuer.current = session.user.id
        // Scheitert der Vormerkungs-Select (vormerkung.error), startet die App
        // normal: die Karenz ist 30 Tage lang, der Dialog kommt beim nächsten
        // Anmelden — ein blockierter Login wäre der größere Schaden.
        const zeile = (vormerkung.data as { frist_ende: string | null }[] | null)?.[0]
        if (zeile) {
          setLoeschung({ fristEnde: zeile.frist_ende })
          setAvv(null); setAvvScope(null); setResolvedUser(session.user.id)
          return
        }
        setLoeschung(null)
        setTenantId(m?.tenantId ?? null)
        setRolle(m?.rolle ?? null)
        // Der Adapter waehlt daran den Satz, den ein Abgewiesener liest.
        setzeStufe(m?.rolle ?? null)
        setResolvedUser(session.user.id)
      })
      .catch((e: Error) => { if (alive) setError(e.message) })
    return () => { alive = false }
  }, [session])

  const enterDemo = useCallback(() => {
    localStorage.setItem(DEMO_KEY, '1')
    setDemo(true)
    // Hier wird NICHT gezählt. Dieser Klick geschieht je Gerät genau einmal —
    // beim nächsten Aufruf stellt `DEMO_KEY` den Demo-Zustand direkt her und
    // `enterDemo` läuft nie wieder. Ein Zähler an dieser Stelle misst deshalb
    // „hat sich irgendwann einmal entschieden" und nicht „schaut heute hin".
    // `demo_open` hängt seit dem 30.07.2026 am Zustand, in `App.tsx`.
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    if (!supabase) throw new Error('Kein Backend konfiguriert')
    setError(null)
    const { error: e } = await supabase.auth.signInWithPassword({ email, password })
    if (e) { setError(e.message); throw new Error(e.message) }
    // Erst nach der erfolgreichen Anmeldung. Ein Zähler, der Fehlversuche
    // mitzählt, beantwortet nicht mehr die Frage „wie viele Menschen nutzen das".
    track('login', false)
    localStorage.removeItem(DEMO_KEY)
    setDemo(false)
  }, [])

  const signUp = useCallback(async (email: string, password: string) => {
    if (!supabase) throw new Error('Kein Backend konfiguriert')
    setError(null)
    const { data, error: e } = await supabase.auth.signUp({ email, password })
    if (e) { setError(e.message); throw new Error(e.message) }
    track('signup_done', false)
    localStorage.removeItem(DEMO_KEY)
    setDemo(false)
    // With email confirmation switched on there is no session yet.
    return { needsConfirmation: !data.session }
  }, [])

  const signOut = useCallback(async () => {
    localStorage.removeItem(DEMO_KEY)
    setDemo(false)
    setTenantId(null)
    setRolle(null)
    setzeStufe(null)
    setLoeschung(null)
    setAvv(null); setAvvScope(null); setAvvSuspended(null); setResolvedUser(null)
    loeschungGeprueftFuer.current = null
    if (supabase) await supabase.auth.signOut()
  }, [])

  const reauth = useCallback(async (password: string) => {
    if (!supabase) throw new Error('Kein Backend konfiguriert')
    const email = session?.user.email
    if (!email) throw new Error('Keine Sitzung')
    const { error: e } = await supabase.auth.signInWithPassword({ email, password })
    if (e) throw new Error(e.message)
  }, [session])

  const loeschungAbbrechen = useCallback(async () => {
    if (!supabase) throw new Error('Kein Backend konfiguriert')
    const { error: e } = await supabase.rpc('konto_loeschung_abbrechen')
    if (e) throw new Error(e.message)
    // Erst den Mandanten laden, dann den Dialog schließen — sonst blitzte auf
    // dem Weg in die App der Onboarding-Zustand auf.
    const m = await findMyTenant(supabase)
    setTenantId(m?.tenantId ?? null)
    setRolle(m?.rolle ?? null)
    setzeStufe(m?.rolle ?? null)
    setAvvScope(null)
    setLoeschung(null)
    setAvvReload(n => n + 1)
  }, [])

  const mode: Mode =
    !ready ? 'loading'
    : session ? (
      loeschung && resolvedUser === session.user.id ? 'loeschung'
      : resolvedUser !== session.user.id ? 'loading'
      : !tenantId ? 'onboarding'
      : avvScope !== scope ? 'loading'
      : avvZustand(avv, rolle) === 'gate' && avvSuspended !== scope ? 'avv'
      : 'live'
    )
    : demo ? 'demo'
    : 'anonymous'

  const value = useMemo<AuthValue>(() => ({
    mode, session, tenantId, rolle, error, loeschung, avv, avvNeuLaden, avvGateAussetzen,
    signIn, signUp, signOut, enterDemo, reauth, loeschungAbbrechen, setTenantId: (id: string) => { setAvvScope(null); setAvvSuspended(null); setTenantId(id); setAvvReload(n => n + 1) },
    clearError: () => setError(null),
  }), [mode, session, tenantId, rolle, error, loeschung, avv, avvNeuLaden, avvGateAussetzen,
    signIn, signUp, signOut, enterDemo, reauth, loeschungAbbrechen])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
